import { useLanguage } from "@/contexts/LanguageContext";
import {
  Award,
  Trophy,
  Crown,
  Sparkles,
  ShieldCheck,
  Clock,
  Building2,
  Target,
  Gift,
  CalendarClock,
  Percent,
  TrendingUp,
  RefreshCw,
} from "lucide-react";
import {
  REBATE_MEDIA_OWNERS,
  TIER_ORDER,
  TIER_LABEL,
  TIER_ACCENT,
  getCommissionPercent,
  type RebateTier,
} from "@/config/affiliateRebates";

const TIER_ICON: Record<RebateTier, typeof Award> = {
  silver: Award,
  gold: Trophy,
  platinum: Crown,
};

export const RebateProgramSection = () => {
  const { language } = useLanguage();
  const isAr = language === "ar";

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background via-primary/5 to-background relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-10 left-1/4 w-72 h-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-secondary/10 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">

        <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-3 flex items-center justify-center gap-3">
          <Sparkles className="h-7 w-7 text-primary" />
          {isAr ? "نظام المكافآت بالقيمة" : "Value Rebate System"}
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-3xl mx-auto">
          {isAr
            ? "تُحتسب المكافآت ربع السنوية بشكل منفصل لكل شريك وسائط. رتبتك مع Alan تعتمد فقط على مبيعات Alan، ورتبتك مع Attention تعتمد فقط على مبيعات Attention، ورتبتك مع On Spot تعتمد فقط على مبيعات On Spot."
            : "Quarterly rewards are calculated separately for each media owner. Your Alan rank is based only on Alan sales, your Attention rank only on Attention sales, and your On Spot rank only on On Spot sales."}
        </p>

        {/* Media owner cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {REBATE_MEDIA_OWNERS.map((owner) => (
            <div
              key={owner.name}
              className="relative rounded-2xl border border-border/60 bg-card overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              {/* Top gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 to-primary/20" />

              {/* Header */}
              <div className="p-6 bg-gradient-to-br from-primary/10 to-transparent border-b border-border/50">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-foreground">{owner.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {isAr
                        ? `الرتبة بناءً على مبيعات ${owner.name} فقط`
                        : `Rank based on ${owner.name} sales only`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Body — per tier */}
              <div className="p-5 space-y-3">
                {/* Base commission badge (Normal tier) */}
                <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
                    <Percent className="h-3.5 w-3.5" />
                    {isAr ? "الرتبة العادية" : "Normal tier"}
                  </div>
                  <div className="text-xs font-bold text-primary tabular-nums">
                    {owner.baseCommissionPercent}% {isAr ? "لكل عملية بيع" : "per sale"}
                  </div>
                </div>

                {TIER_ORDER.map((tier) => {
                  const Icon = TIER_ICON[tier];
                  const accent = TIER_ACCENT[tier];
                  const label = TIER_LABEL[tier][isAr ? "ar" : "en"];
                  const threshold = owner.thresholds[tier];
                  const reward = owner.screenRewards[tier];
                  const discountCap = owner.discount?.capByTier[tier];
                  const commissionPct = getCommissionPercent(owner, tier);

                  const nextTier = TIER_ORDER[TIER_ORDER.indexOf(tier) + 1] as RebateTier | undefined;
                  const nextThreshold = nextTier ? owner.thresholds[nextTier] : undefined;
                  const gap = nextThreshold !== undefined ? nextThreshold - threshold : 0;

                  return (
                    <div
                      key={tier}
                      tabIndex={0}
                      className={`group relative rounded-lg border border-border/60 bg-muted/30 p-3 ring-1 ${accent.ring} cursor-help transition-all hover:bg-muted/60 hover:ring-2 hover:shadow-md focus-within:bg-muted/60 focus-within:ring-2 focus-within:shadow-md outline-none`}
                    >
                      <div className={`absolute top-0 left-0 right-0 h-0.5 rounded-t-lg bg-gradient-to-r ${accent.from} ${accent.to}`} />

                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-md bg-card ${accent.text}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <span className={`text-xs font-bold uppercase tracking-widest ${accent.text}`}>
                            {label}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] text-muted-foreground leading-tight">
                            {isAr ? "الحد الأدنى" : "Threshold"}
                          </div>
                          <div className="text-sm font-bold text-foreground tabular-nums">
                            ≥ {threshold.toLocaleString()} {isAr ? "ر.س" : "SAR"}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-border/40">
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {isAr
                            ? `${reward.durationWeeks} أسبوعين`
                            : `${reward.durationWeeks} weeks`}
                        </div>
                        <div className="text-sm font-bold text-foreground tabular-nums">
                          {reward.screens}{" "}
                          <span className="text-xs font-normal text-muted-foreground">
                            {isAr ? "شاشة" : "screens"}
                          </span>
                        </div>
                      </div>

                      <div className="mt-2 flex items-center justify-between rounded-md bg-primary/5 border border-primary/20 px-2 py-1.5">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-primary">
                          <Percent className="h-3 w-3" />
                          {isAr ? "العمولة" : "Commission"}
                        </div>
                        <div className="text-xs font-bold text-primary tabular-nums">
                          {commissionPct}% {isAr ? "لكل عملية بيع" : "per sale"}
                        </div>
                      </div>

                      {discountCap !== undefined && (
                        <div className="mt-2 text-[11px] text-primary font-medium">
                          {isAr
                            ? `+ خصم حتى ${discountCap}%`
                            : `+ up to ${discountCap}% discount`}
                        </div>
                      )}

                      {/* Inline-expanding details */}
                      <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] focus-within:grid-rows-[1fr] transition-all duration-300 ease-out">
                        <div className="overflow-hidden">
                          <div className="mt-3 pt-3 border-t border-border/40 space-y-2 text-xs">
                            <div className="flex items-start gap-2">
                              <Target className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                              <span className="text-foreground">
                                {isAr
                                  ? `≥ ${threshold.toLocaleString()} ر.س من مبيعات ${owner.name} في الربع`
                                  : `≥ ${threshold.toLocaleString()} SAR in ${owner.name} sales per quarter`}
                              </span>
                            </div>

                            <div className="flex items-start gap-2">
                              <Percent className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                              <span className="text-foreground">
                                {isAr
                                  ? `يحصل المسوق على ${commissionPct}% من كل عملية بيع مع ${owner.name} في هذه الرتبة`
                                  : `Affiliate earns ${commissionPct}% of each sale with ${owner.name} at this tier`}
                              </span>
                            </div>

                            <div className="flex items-start gap-2">
                              <Gift className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                              <span className="text-foreground">
                                {isAr
                                  ? `${reward.screens} شاشة مجانية مع ${owner.name}`
                                  : `${reward.screens} free screens with ${owner.name}`}
                              </span>
                            </div>

                            <div className="flex items-start gap-2">
                              <Clock className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                              <span className="text-foreground">
                                {isAr
                                  ? `قابلة للاستخدام لمدة ${reward.durationWeeks} أسبوعين`
                                  : `Usable for ${reward.durationWeeks} weeks`}
                              </span>
                            </div>

                            <div className="flex items-start gap-2">
                              <CalendarClock className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                              <span className="text-foreground">
                                {isAr
                                  ? "تنتهي صلاحيتها بعد عام واحد من تاريخ المنح"
                                  : "Expires 1 year from grant date"}
                              </span>
                            </div>

                            {discountCap !== undefined && (
                              <div className="flex items-start gap-2">
                                <Percent className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                                <span className="text-foreground">
                                  {isAr
                                    ? `خصم حتى ${discountCap}% على الحملات المستقبلية مع ${owner.name}`
                                    : `Up to ${discountCap}% discount on future campaigns with ${owner.name}`}
                                </span>
                              </div>
                            )}

                            {nextTier && nextThreshold !== undefined && (
                              <div className="flex items-start gap-2 pt-2 border-t border-border/40">
                                <TrendingUp className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                                <span className="text-muted-foreground">
                                  {isAr
                                    ? `حقق ${gap.toLocaleString()} ر.س إضافية في مبيعات ${owner.name} لفتح رتبة ${TIER_LABEL[nextTier].ar}`
                                    : `Reach ${gap.toLocaleString()} more SAR in ${owner.name} sales to unlock ${TIER_LABEL[nextTier].en}`}
                                </span>
                              </div>
                            )}

                            <div className="flex items-start gap-2">
                              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                              <span className="text-muted-foreground">
                                {isAr ? "تُعاد الرتب كل ربع سنة" : "Tiers reset every quarter"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footnote */}
        <div className="bg-card/60 border border-border/50 rounded-xl p-5 backdrop-blur-sm text-sm text-muted-foreground space-y-2 max-w-3xl mx-auto">
          <p className="flex items-start gap-2">
            <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            {isAr
              ? "جميع مكافآت الشاشات قابلة للاستخدام لمدة أسبوعين، وتنتهي صلاحيتها بعد عام واحد من تاريخ منحها."
              : "All screen rewards are usable for 2 weeks and expire 1 year after being granted."}
          </p>
          <p className="flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            {isAr
              ? "بعض شركاء الوسائط غير مشمولين بهذا النظام حالياً. سيتم تحديد بعض أنظمة الخصم لاحقاً."
              : "Some media partners are not included in this program yet. Certain discount systems are still TBD."}
          </p>
        </div>
      </div>
    </section>
  );
};

export default RebateProgramSection;
